import type { CompanionManifest, CompanionState, Direction } from '@/lib/companion/types';
import { ROW_ALIASES } from '@/lib/companion/row-taxonomy';

export interface ResolvedAnimation {
  row: number;
  frames: number;
  mirror: boolean;
  rotate: boolean;
  loop: boolean;
  pingPong?: boolean;
}

/** How many steps a cycle takes. Ping-pong walks up and back without
 *  repeating either end, so it is one step short of twice the frame count. */
export function pingPongSpan(count: number, pingPong?: boolean): number {
  if (!pingPong || count <= 2) return count;
  return (count - 1) * 2;
}

/** Reflect a monotonic step counter onto a frame column. Art whose last frame
 *  sits far from its first snaps visibly when a cycle wraps; walking back down
 *  keeps every transition between adjacent frames. */
export function pingPongFrame(step: number, count: number, pingPong?: boolean): number {
  if (!pingPong || count <= 2) return step % count;
  const span = (count - 1) * 2;
  const phase = ((step % span) + span) % span;
  return phase < count ? phase : span - phase;
}

function lookup(
  manifest: CompanionManifest,
  name: string,
): { row: number; frames: number } | null {
  for (const alias of ROW_ALIASES[name] ?? [name]) {
    const entry = manifest.animations[alias];
    if (entry && entry.frames > 0) return entry;
  }
  return null;
}

function idle(manifest: CompanionManifest): { row: number; frames: number } {
  return lookup(manifest, 'idle') ?? { row: 0, frames: 1 };
}

/**
 * Pick the locomotion row for a travel direction. Sheets with dedicated
 * directional rows already face the right way; a sheet carrying only a single
 * `running` row faces left by convention, so rightward travel mirrors.
 */
function locomotion(
  manifest: CompanionManifest,
  direction: Direction,
): { row: number; frames: number; mirror: boolean } | null {
  const preferred = direction >= 0 ? 'running-right' : 'running-left';
  const directional = manifest.animations[preferred];
  if (directional && directional.frames > 0) {
    return { ...directional, mirror: false };
  }

  const opposite = direction >= 0 ? 'running-left' : 'running-right';
  const flipped = manifest.animations[opposite];
  if (flipped && flipped.frames > 0) {
    return { ...flipped, mirror: true };
  }

  const generic = lookup(manifest, 'running');
  if (generic) return { ...generic, mirror: direction >= 0 };

  return null;
}

/** Non-locomotion states: which row they draw from and whether it loops.
 *  A state absent here (idle, dragged) always loops and falls back to idle
 *  when its row is missing from the manifest.
 *  Sleeping and sitting do NOT loop: each plays down into its pose and holds
 *  there, so the companion stays asleep or seated until something moves it
 *  on, rather than replaying the act of settling every cycle.
 *  Exported so a manifest-integrity test can assert every target name
 *  actually exists in the bundled Nula pet.json — a row rename on the art
 *  side silently degrades to idle otherwise. */
export const STATE_ANIMATION: Partial<Record<CompanionState, { name: string; loop: boolean }>> = {
  reacting: { name: 'waving', loop: true },
  falling: { name: 'jumping', loop: true },
  sitting: { name: 'sit', loop: false },
  sleeping: { name: 'sleep', loop: false },
  working: { name: 'working', loop: true },
  reading: { name: 'reading', loop: true },
  drinking: { name: 'boba', loop: true },
  lookingAround: { name: 'look-around', loop: false },
  stretching: { name: 'stretch', loop: false },
  wakingUp: { name: 'wake-up', loop: false },
  held: { name: 'held', loop: true },
  landing: { name: 'landing', loop: false },
  playingWithOrb: { name: 'play-with-orb', loop: false },
  peeking: { name: 'peek', loop: false },
  celebrating: { name: 'celebrate', loop: false },
  affectionate: { name: 'affection', loop: false },
  failed: { name: 'failed', loop: false },
};

export function resolveAnimation(
  manifest: CompanionManifest,
  state: CompanionState,
  direction: Direction,
): ResolvedAnimation {
  if (state === 'walking' || state === 'climbing') {
    const moving = locomotion(manifest, direction);
    const base = moving ?? { ...idle(manifest), mirror: false };
    return { ...base, rotate: state === 'climbing', loop: true };
  }

  const mapped = STATE_ANIMATION[state];
  const found = mapped ? lookup(manifest, mapped.name) : null;
  const loop = found ? mapped!.loop : true;

  return {
    ...(found ?? idle(manifest)),
    mirror: false,
    rotate: false,
    loop,
    // Whatever ends up drawing the idle row breathes rather than cycling: the
    // art's last frame sits further from its first than any step inside the
    // cycle, so a plain wrap snaps. Walking back down keeps it continuous.
    pingPong: (found ?? idle(manifest)).row === idle(manifest).row,
  };
}
